import React from "react";
import styles from "./GroupedEventItem.module.css";
import { DigestItem as DigestItemType, GroupedEvent, InviteStatus, ResponseType, ViewerRelationshipType } from "../../gql/types";
import { ActionType } from "components/action/types";
import { sanitizeActionTypes } from "./groupedEventItemSanitization";
import MainEventItem from "./MainEventItem";
import PlaydateInviteSimpleWorkflowCard from "./PlaydateInviteSimpleWorkflowCard";

type GroupedEventItemProps = {
    item: DigestItemType;
};

type AdditionalContext = {
    UserAction?: {
        [eventID: string]: string[]; // Map of event IDs to a list of action types
    };
};

const GroupedEventItem: React.FC<GroupedEventItemProps> = ({ item }) => {
    const groupedEvent = item.content as GroupedEvent;
    const sanitizedUserActionMap = item.additionalContext ? sanitizeActionTypes(item.additionalContext as AdditionalContext) : null;

    const formatTime = (dateString: string) => {
        const date = new Date(dateString);
        const options: Intl.DateTimeFormatOptions = {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        };
        return date.toLocaleTimeString(undefined, options);
    };

    const eventActions = (eventID: string): ActionType[] => {
        return sanitizedUserActionMap && sanitizedUserActionMap[eventID]?.length > 0
            ? sanitizedUserActionMap[eventID]
            : [];
    };

    return (
        <div className={`${styles.digestItem}`}>
            {groupedEvent.mainEvents && (
                <div className={styles.mainEventSection}>
                    {groupedEvent.mainEvents.map((event) => {
                        return (
                            <MainEventItem
                                key={event.eventID}
                                event={event}
                                possibleActions={eventActions(event.eventID)}
                                hasNoConfirmedPlaydateInvites={
                                    (groupedEvent.playdateInvites?.filter(
                                        invite => invite.status === InviteStatus.Confirmed &&
                                            invite.viewerResponse &&
                                            invite.viewerResponse !== null &&
                                            invite.viewerResponse.responseType == ResponseType.Accept
                                    ).length ?? 0) === 0}
                            />);
                    }
                    )}
                </div>
            )}

            {groupedEvent.relatedEvents && (
                <div className={styles.relatedEventSection}>
                    {groupedEvent.relatedEvents.map((event) => (
                        <div key={event?.eventID} className={`${styles.eventItem} ${event?.owner.viewerRelationship == ViewerRelationshipType.Spouse ? styles.spouseRelatedEventItem : styles.relatedEventItem}`}>
                            <div className={styles.eventItemLeftSection}>
                                {event?.owner.viewerRelationship !== ViewerRelationshipType.Self && event.owner.userSummary?.profilePictureUrl &&
                                    <div className={styles.eventItemProfilePictureContainer}>
                                        <img src={event.owner.userSummary.profilePictureUrl} className={styles.eventItemProfilePicture} />
                                    </div>
                                }
                                <div className={styles.evenItemtTitle}>{event.title || "Related Event"}</div>
                            </div>
                            <div className={styles.eventItemRightSection}>
                                {event.owner.viewerRelationship !== ViewerRelationshipType.Self &&
                                    <div className={styles.eventItemCreator}>
                                        Created by: {event.owner.userSummary?.firstName // Check if firstName exists
                                            ? event.owner.userSummary.firstName
                                            : event.owner.viewerRelationship === 'SPOUSE' // Check if viewerRelationship is SPOUSE
                                                ? 'spouse'
                                                : 'other member' // Fallback if neither condition is met
                                        }
                                    </div>
                                }
                                <div className={styles.eventItemTime}>
                                    {formatTime(event.startDate)}{event.endDate ? ` - ${formatTime(event.endDate)}` : ""}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {groupedEvent.otherEvents && (
                <div className={styles.otherEventSection}>
                    {groupedEvent.otherEvents.map((event) => (
                        <div key={event.eventID} className={`${styles.otherEventItem} ${styles.eventItem}`}>
                            <div className={styles.eventItemLeftSection}>
                                <div className={styles.evenItemtTitle}>{event.title || "Other Event"}</div>
                            </div>
                            <div className={styles.eventItemRightSection}>
                                <div className={styles.eventItemTime}>
                                    {formatTime(event.startDate)}{event.endDate ? ` - ${formatTime(event.endDate)}` : ""}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {groupedEvent.playdateInvites && (
                <div className={styles.playdatesSections}>
                    {groupedEvent.playdateInvites.map((invite, index) => (
                        <React.Fragment key={`${invite.inviteID}-${index}`}>
                            <PlaydateInviteSimpleWorkflowCard
                                invite={invite}
                            />
                        </React.Fragment>
                    ))}
                </div>
            )}

        </div>
    );
};

export default GroupedEventItem;
